import { FolderTree, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { DataTable, useDataTable } from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { BlogCategory } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { useBlogCategoryColumns } from '../components/blog-category-columns';
import { BlogCategoryDialog } from '../components/blog-category-dialog';
import { useBlogCategoryRowActions } from '../components/blog-category-row-actions';
import { DeleteBlogCategoryDialog } from '../components/delete-blog-category-dialog';
import { useBlogCategories } from '../hooks/use-blog-categories';

/** `category: null` adds a new one. */
interface Editing {
  category: BlogCategory | null;
}

const EMPTY: readonly BlogCategory[] = [];

function BlogCategories() {
  const { t } = useTranslation('blog');
  const store = useStore();
  const { can } = usePermissions();
  const query = useBlogCategories(store.id);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deleting, setDeleting] = useState<BlogCategory | null>(null);
  const canWrite = can('content.write');
  const onEdit = useCallback((category: BlogCategory) => setEditing({ category }), []);
  const columns = useBlogCategoryColumns();
  const rowActions = useBlogCategoryRowActions({ onEdit, onDelete: setDeleting });
  const rows = query.data ?? EMPTY;
  const table = useDataTable({ data: rows, columns, getRowId: (row) => row.id });
  const nextPosition = rows.reduce((max, c) => Math.max(max, c.position + 1), 0);

  const addButton = (tour?: string) => (
    <Can permission="content.write">
      <Button onClick={() => setEditing({ category: null })} data-tour={tour}>
        <Plus aria-hidden />
        {t('categories.new')}
      </Button>
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('categories.title')}
        description={t('categories.description')}
        backTo={{ to: '/s/$storeId/content/blog', params: { storeId: store.id } }}
        backLabel={t('posts.title')}
        actions={addButton('content.blog-categories.new')}
        tour="content.blog-categories.header"
      />
      {query.isSuccess && rows.length === 0 ? (
        <EmptyState
          icon={FolderTree}
          title={t('categories.empty.title')}
          description={t('categories.empty.description')}
          action={addButton()}
          tour="content.blog-categories.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          tour={{ table: 'content.blog-categories.table' }}
          search={{ label: t('categories.search'), placeholder: t('categories.search') }}
          rowActions={canWrite ? rowActions : undefined}
          onRowClick={canWrite ? onEdit : undefined}
          getRowLabel={(row) => row.title}
          resourceName={{
            singular: t('categories.resource_one'),
            plural: t('categories.resource_other'),
          }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      )}
      <BlogCategoryDialog
        storeId={store.id}
        open={editing !== null}
        category={editing?.category ?? null}
        nextPosition={nextPosition}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
      <DeleteBlogCategoryDialog
        storeId={store.id}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function BlogCategoriesPage() {
  return (
    <FeatureErrorBoundary name="blog.categories">
      <BlogCategories />
    </FeatureErrorBoundary>
  );
}
